import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '../../../../hooks/useToast';
import {
  listTtsVoices,
  selectSceneNarration,
  updateScene,
} from '../../../../features/scenarios/api';
import type { Scenario, ScenarioResponse } from '../types';
import { defaultLicensedVoiceFilter, providerSupportsVoiceFilters, type NarrationProviderCode } from '../constants';
import { ttsVoicesQueryKey, TTS_VOICES_STALE_TIME } from '../narrationVoiceProvider';
import { useSceneStateContext } from './SceneStateContext';
import { readStoredNarrationProvider, writeStoredNarrationProvider } from '../narrationProviderStorage';
import { useNarrationCapabilities } from './useNarrationCapabilities';
import { normalizeDialect } from '../../state/dialect';

type Voice = {
  voice_id: string;
  name: string;
  labels: Record<string, string>;
  preview_url?: string;
  category?: string;
  high_quality_base_model_ids?: string[];
};

interface UseNarrationHandlersParams {
  lastResult: ScenarioResponse | null;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  activeScenarioId: string | null;
  scenarios: Scenario[];
  // シナリオの方言 (option key) → ナレーターの accent (Gemini 拡張ボイスライブラリの API 生値)
  dialectVoiceAccents?: Record<string, string>;
}

export function useNarrationHandlers({
  lastResult,
  setLastResult,
  activeScenarioId,
  scenarios,
  dialectVoiceAccents,
}: UseNarrationHandlersParams) {
  const { setSceneVideoMaterialId } = useSceneStateContext();
  const toast = useToast();

  // ナレーション関連state
  // 音声AIモデル。ユーザーの選択 (UI 操作) は localStorage に保持し、リロード後も維持する。
  // 保存済みシナリオの声からの自動復元 (restoreNarrationProvider) は保存しない
  // (古いシナリオを開いただけで既定が変わらないように)。
  const [selectedNarrationProvider, restoreNarrationProvider] = useState<NarrationProviderCode>(readStoredNarrationProvider);
  // 方言付きシナリオで一時的に Gemini へ切り替えた状態か (ユーザーが選び直したら解除)
  const dialectProviderOverrideRef = useRef(false);
  const setSelectedNarrationProvider = useCallback<React.Dispatch<React.SetStateAction<NarrationProviderCode>>>((action) => {
    dialectProviderOverrideRef.current = false;
    restoreNarrationProvider((prev) => {
      const next = typeof action === 'function' ? action(prev) : action;
      writeStoredNarrationProvider(next);
      return next;
    });
  }, []);
  const [recommendedNarrator, setRecommendedNarrator] = useState<{ gender?: string; age?: string } | null>(null);
  // 表示中シナリオの方言に対応するナレーターの accent (API 生値)。'' は方言なし / 対応する声なし
  const activeScenario = scenarios.find(s => s.id === activeScenarioId) ?? null;
  const activeDialect = normalizeDialect(activeScenario?.generation_metadata as Record<string, unknown> | undefined);
  const dialectAccent = (activeDialect && dialectVoiceAccents?.[activeDialect]) || '';
  const geminiCapabilities = useNarrationCapabilities('gemini', { enabled: !!dialectAccent });
  // 推奨ナレーターの絞り込み可否は BE の今の既定モデルで判定する (Gemini は 3.8 ⇔ 3.1 旧経路で変わる)
  const narrationCapabilities = useNarrationCapabilities(selectedNarrationProvider);
  // 推奨ナレーター (性別・年代) で声を絞れるか。Gemini は 3.8 経路でライブラリの声が入るときだけ
  const voiceFiltersSupported = narrationCapabilities.voice_filters ?? providerSupportsVoiceFilters(selectedNarrationProvider);
  // 方言付きシナリオでは、ナレーションを Gemini (地域の声で絞り込み) に切り替える。
  // - 切り替えは localStorage に保存しない (方言なしのシナリオでは前回選んだプロバイダに戻す)
  // - シナリオに生成済みナレーションがあれば、その声の復元 (useScenarioGeneration) を優先する
  // - 切り替え後にユーザーがプロバイダを選び直したら、そのシナリオでは再適用しない
  // - Gemini が 3.1 旧経路 (地域の声が使えない) の間は切り替えない
  const dialectAppliedScenarioRef = useRef<string | null>(null);
  const hasSavedNarrationVoice = !!activeScenario?.scenes?.some(sc => sc.narration_voice_id);
  useEffect(() => {
    if (!dialectAccent) {
      dialectAppliedScenarioRef.current = null;
      if (dialectProviderOverrideRef.current) {
        dialectProviderOverrideRef.current = false;
        restoreNarrationProvider(readStoredNarrationProvider());
      }
      return;
    }
    if (!activeScenarioId || dialectAppliedScenarioRef.current === activeScenarioId || !geminiCapabilities.known) return;
    dialectAppliedScenarioRef.current = activeScenarioId;
    if (!geminiCapabilities.voice_filters || hasSavedNarrationVoice) return;
    dialectProviderOverrideRef.current = true;
    restoreNarrationProvider('gemini');
  }, [dialectAccent, activeScenarioId, geminiCapabilities.known, geminiCapabilities.voice_filters, hasSavedNarrationVoice]);
  // ナレーター一覧の既定の地域絞り込み。Gemini の声だけが accent を持つ
  const narratorAccent = selectedNarrationProvider === 'gemini' && voiceFiltersSupported ? dialectAccent : '';
  // 「英語で発音する」(三点メニュー)。サウンドロゴ等の英語ネイティブ読み用。
  // シーン個別 (sceneKey で引く)。「すべてのナレーションを生成」も各シーンの値を scene_languages で送る。
  const [narrationEnglishScenes, setNarrationEnglishScenes] = useState<Record<string, boolean>>({});
  // 「すべてのナレーションを自動生成」は設定 UI から隠した (標準フローは「ナレーション」セクションの生成)。
  // 過去に localStorage へ保存された true が残っていても、UI で解除できないまま裏で走らないよう
  // 常に false から始める (保存値は読まない)。旧経路 (1 声の一括生成) の撤去に伴い自動生成の実行側は外した
  // (state だけ設定画面の配線のために残す)。
  const [autoGenerateNarration, setAutoGenerateNarration] = useState(false);

  // ボイス一覧取得 (プロバイダ別・無条件)。プロバイダ切替時はキーが変わり再取得される。
  // API 側で絞るプロバイダ (Fish Audio) では、VoiceSelect が絞り込み条件ごとの一覧を別キーで
  // 取得する。ここでの voicesData は話者ごとの声の選択肢の下地と表示名 (voiceNameMap) に使う。
  // 「権利処理済み」が既定オンのブランド (SAITE) では権利処理済みに限る
  // (VoiceSelect の既定状態と同じ queryKey になり、取得も共有される)
  const baseVoiceFilters = useMemo(
    () => ({ licensed: defaultLicensedVoiceFilter(selectedNarrationProvider) || undefined }),
    [selectedNarrationProvider],
  );
  const { data: voicesData, isLoading: voicesLoading } = useQuery({
    queryKey: ttsVoicesQueryKey(selectedNarrationProvider, baseVoiceFilters),
    queryFn: async () => {
      return await listTtsVoices<Voice>(selectedNarrationProvider, baseVoiceFilters);
    },
    staleTime: TTS_VOICES_STALE_TIME,
  });

  // voice_id → 表示名。履歴には別プロバイダで生成した素材も並ぶため、
  // 切替時に上書きせず両プロバイダ分を蓄積する。
  const [voiceNameMap, setVoiceNameMap] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!voicesData?.length) return;
    setVoiceNameMap(prev => {
      const next = { ...prev };
      for (const v of voicesData) {
        next[v.voice_id] = v.name;
      }
      return next;
    });
  }, [voicesData]);

  const handleSelectNarration = useCallback(async (sceneId: string, materialId: string, voiceId?: string, durationSeconds?: number) => {
    try {
      await selectSceneNarration(sceneId, materialId);
      // ローカルステートを更新
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map(s => ({
            ...s,
            scenes: s.scenes.map(sc =>
              sc.id === sceneId
                ? { ...sc, narration_material_id: materialId, ...(voiceId !== undefined ? { narration_voice_id: voiceId } : {}), narration_duration_seconds: durationSeconds, narration_excluded: false }
                : sc
            ),
          }));
          return { ...prev, scenarios: updatedScenarios };
        });
      }
      toast.success('ナレーション音声を切り替えました');
    } catch {
      toast.error('ナレーション音声の切り替えに失敗しました');
    }
  }, [lastResult, setLastResult, toast]);

  // シーンの前面音声の種別を切り替える (narration / sound_logo)。
  // 非選択側のデータは破壊しない (種別だけ切替えてパイプライン側で無視する)。
  const handleSetAudioTrackType = useCallback(async (sceneId: string, type: 'narration' | 'sound_logo') => {
    try {
      await updateScene(sceneId, { audio_track_type: type });
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map(s => ({
            ...s,
            scenes: s.scenes.map(sc =>
              sc.id === sceneId ? { ...sc, audio_track_type: type } : sc
            ),
          }));
          return { ...prev, scenarios: updatedScenarios };
        });
      }
    } catch {
      toast.error('音声種別の切り替えに失敗しました');
    }
  }, [lastResult, setLastResult, toast]);

  // 前面音声 (ナレーション/サウンドロゴ) の「採用しない」トグル。
  // 素材紐付けは保持したまま、プレビューMP4合成・エクスポートの対象から外す。
  const handleToggleNarrationExcluded = useCallback(async (sceneId: string, excluded: boolean) => {
    try {
      await updateScene(sceneId, { narration_excluded: excluded });
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map(s => ({
            ...s,
            scenes: s.scenes.map(sc =>
              sc.id === sceneId ? { ...sc, narration_excluded: excluded } : sc
            ),
          }));
          return { ...prev, scenarios: updatedScenarios };
        });
      }
      toast.success(excluded ? 'ナレーションをプレビュー・エクスポートから除外しました' : 'ナレーションを採用に戻しました');
    } catch {
      toast.error('ナレーションの採用状態の変更に失敗しました');
    }
  }, [lastResult, setLastResult, toast]);

  // シーンにサウンドロゴ素材を紐付ける (種別も sound_logo に切替)。
  const handleSelectSoundLogo = useCallback(async (sceneId: string, materialId: string, durationSeconds?: number) => {
    try {
      await updateScene(sceneId, {
        audio_track_type: 'sound_logo',
        sound_logo_material_id: materialId,
      });
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map(s => ({
            ...s,
            scenes: s.scenes.map(sc =>
              sc.id === sceneId
                ? { ...sc, audio_track_type: 'sound_logo' as const, sound_logo_material_id: materialId, sound_logo_duration_seconds: durationSeconds }
                : sc
            ),
          }));
          return { ...prev, scenarios: updatedScenarios };
        });
      }
      toast.success('サウンドロゴを設定しました');
    } catch {
      toast.error('サウンドロゴの設定に失敗しました');
    }
  }, [lastResult, setLastResult, toast]);

  const handleMergeComplete = useCallback((sceneId: string, sceneKey: string, materialId: string, durationSeconds?: number) => {
    setSceneVideoMaterialId((prev) => ({ ...prev, [sceneKey]: materialId }));
    if (lastResult) {
      setLastResult((prev) => {
        if (!prev) return prev;
        const updatedScenarios = prev.scenarios.map(s => ({
          ...s,
          scenes: s.scenes.map(sc =>
            sc.id === sceneId
              ? { ...sc, video_material_id: materialId, video_duration_seconds: durationSeconds != null ? Math.round(durationSeconds) : durationSeconds, video_has_audio: true }
              : sc
          ),
        }));
        return { ...prev, scenarios: updatedScenarios };
      });
    }
  }, [lastResult, setLastResult, setSceneVideoMaterialId]);

  return {
    // state
    selectedNarrationProvider,
    setSelectedNarrationProvider,
    restoreNarrationProvider,
    recommendedNarrator,
    setRecommendedNarrator,
    narratorAccent,
    narrationEnglishScenes,
    setNarrationEnglishScenes,
    autoGenerateNarration,
    setAutoGenerateNarration,
    voicesData,
    voicesLoading,
    voiceNameMap,
    // handlers
    handleSelectNarration,
    handleSetAudioTrackType,
    handleSelectSoundLogo,
    handleToggleNarrationExcluded,
    handleMergeComplete,
  };
}
